// ts-nocheck

import React, {useState, useEffect} from 'react';
import {Link} from "react-router-dom";

import CountryFlagAndName from '../../components/countryFlagAndName.js'
import DomainTable from '../../components/domainTable.js'
import GeoDistribution from '../../components/geoDistribution.js'
import TechnologySmallIcon from '../../components/technologySmallIcon.js'
import VersionRef from '../../components/versionRef.js'
import imageOnError from '../../helpers/imgOnError.js';
import TechnologyMarketShare from '../../components/technologyChart.js'
import DistributionByPopularityChart from '../../components/technologyDistributionChart.js'
import useGoogleCharts from '../../components/useGoogleCharts.js';
import {_getCoutryGoogleChartName} from '../../helpers/helpers.js'
import CountriesLeadList from '../../components/countriesLeadList.js'
import TLDLeadList from '../../components/tldLeadList.js'
import FAQ from '../../components/faqComponent.js'
import CopyLink from '../../components/copyLink.js'
import CveTitle from '../../components/cveTitle.js'
import {DataSetTechnologyData} from '../../components/structuredData.js'
import {
	_printDate,
	_printShortDate,
	_printNumber, 
	_printPercent, 
	_extractWebsiteFromURL,
	_isGoogleIcon,
	_getGoogleIcon
	} from '../../helpers/helpers'

import {CVEData, TechnologyVersion, TechnologyInfoPageResult, TechnologyInfoWithDomains, TechnologyInfoNoDomains} from '../../../../server/routes/technology.js'

interface ComponentProps {
	props: TechnologyInfoPageResult;
}

interface ComponentPropsWithDomains {
	props: TechnologyInfoWithDomains;
}

interface ComponentPropsWithGoogle extends ComponentPropsWithDomains {
	google: wts_approved_any;
}

interface CVERowProps {
	cve: CVEData;
	requireAuthentication?: boolean;
}

interface LeadListProps {
	props: TechnologyInfoPageResult;
	title: string;
	count: string;
}

export default function Technology({server, pageData}: {server: string, pageData: unknown}) {

	const typedPageData = pageData as TechnologyInfoPageResult;
	let [data, setData] = useState(typedPageData);
	const google = useGoogleCharts();

	return (
		<>
			<DataSetTechnologyData technology={data.TechnologyLongName} url={data.TechnologyURL} />
			<section className="section section-sm">
				<div className="container">
					<div className="row row-50">
						<div className="col-md-8 col-lg-8 col-xl-8">
							<TechnologyLeftPanel props={data}  google={google}/>
						</div>
						<div className="col-md-4 col-lg-4 col-xl-4">
							<TechnologyRightPanel props={data}/>
						</div>					
					</div>
					<TechnologyBottomPanel props={data} google={google} />
				</div>
			</section>
		</>
	)
};

function NoDomainTechnology({props}: {props: TechnologyInfoNoDomains}) { 

	return (
		<div className="row row-50">
			<div className="col-12">
				<h4>We did not find any domains that use {props.TechnologyLongName} </h4>
			</div>
		</div>
	)
}

function TechnologyLeftPanel({props, google}: {props: TechnologyInfoPageResult, google: wts_approved_any}) {
	return (
		<>
			<h2 style={{wordWrap: 'break-word'}}>{props.TechnologyLongName}</h2>
			Category - {' '}
			{
				(props.categories || []).map((category, index) => {
					return (
						<span key={category.CategoryShortName}>
							{!!index && ', '}
							<b>
								<Link 
									to={`/technology-type/${category.CategoryURL}`}
									className="link-hover" 
									title={`${category.CategoryShortName} category`} 
								>
									{category.CategoryShortName} 
								</Link>
							</b>
						</span>
					)
				})	
			}
			<p>
				{props.technologyDescription}
			</p>
			<hr/>
			{
				props.NoDomainsFound && 
					<NoDomainTechnology props={props}/>
			}
			{
				!props.NoDomainsFound &&
					<HasDomainTechnology props={props} google={google}/>
			}
		</>
	)
}

function TechnologyLargeIcon({props}: ComponentProps) {

	return (
		<>
			{
				props.TechnologyIcon && _isGoogleIcon(props.TechnologyIcon) &&
					<TechnologyLargeGoogleIcon props={props} />
			}
			{
				props.TechnologyIcon && !_isGoogleIcon(props.TechnologyIcon) &&
					<TechnologyLargeInternalIcon props={props} />
			}
			{
				!props.TechnologyIcon &&
					<TechnologyLargeNoIcon />
			}
		</>
	)
}

function TechnologyLargeGoogleIcon({props}: ComponentProps) {
	return (
		<img 
			width="32"
			height="32" 
			style={{width: '32px'}}
			src={`https://www.google.com/s2/favicons?domain=${_getGoogleIcon(props.TechnologyIcon)}`} 
			onError={imageOnError}
		/>    
	)
}

function TechnologyLargeInternalIcon({props}: ComponentProps) {
	// Dont use newsrc (lazyeload) for large icons, 
	// as they placed on the visible top screen

	// use /til/ - technology icon large location here
	return (
		<img 
			height='100' width='100'
			style={{width: '100px'}} 
			title={props.TechnologyShortName}
			src={`/s/til/${props.TechnologyIcon}`} 
		/>
	)
}

function TechnologyLargeNoIcon(): null {
	// we do not show default icon as large icon
	return null
}

function TechnologyRightIcon({props}: ComponentProps) {

	return (
		<>
			{ 
				props.TechnologyIcon &&
				<>
					<article className="box-bodrered">
						<TechnologyLargeIcon props={props}/>
					</article>
				</>
			}
		</>
	)
}

function TechnologyAvailableReports({props}: {props: TechnologyInfoPageResult}) {
	return (
		<>
			<br/>
			<h4>Available Reports</h4>
			<div>
				<ul>
					<li>
						<Link 
							to={`/technology/${props.TechnologyURL}/websites`}
							className="link-hover" 
							title={`List of websites that are using ${props.TechnologyShortName} technology`}
						>
							Website List
						</Link>
					</li>       
					{
						!props.NoDomainsFound && props.TechnologyDevelopments &&                                       
						<li>
							<Link 
								to={`/technology/${props.TechnologyURL}/changes`} 
								className="link-hover" 
								title={`${props.TechnologyShortName} adoption report`}
							>
								Usage Changes
							</Link>
						</li>
					}
					{ 
						!props.NoDomainsFound && !!props.TechnologySubVersions && props.TechnologySubVersions.length > 0 &&            
						<li>
							<Link 
								to={`/technology/${props.TechnologyURL}/versions`} 
								className="link-hover" 
								title={`Show all versions of ${props.TechnologyLongName}`}
							>
								{props.TechnologyShortName} Versions
							</Link>                                      
						</li>
					}                                         
				</ul>      
			</div>
		</>
	)
}

function TechnologyAlternativeNames({props}: ComponentProps) {
	return (
		<>
			{
				props.altNames && props.altNames.length > 0 &&
				<>
					<br/>
					<h4>Alternative Names</h4>
					<ul className="list-marked">
						{
							props.altNames.map((altName: string, indexKey: number) => {
								return <li  key={indexKey}>{altName}</li>
							})
						}
					</ul>                
				</>
			}
		</>
	)
}

function TechnologyInformation({props}: ComponentProps) {
	return (
		<>
			<br/>
			<h4>Technology Information</h4>
			<ul className="list-marked">
				<li>Name - {props.TechnologyLongName}</li>
				<li>Category :
				{
					props.categories.map((category, indexKey) => {
						return (
							<b key={indexKey}>
								{!!indexKey && ', '}
								<Link 
									key={indexKey}
									to={`/technology-type/${category.CategoryURL}`}
									className="link-hover" 
									title={category.CategoryShortName} 
								>
									{' ' + category.CategoryLongName} 
								</Link>
							</b>
						)
					})
				}
				</li>
				<li>
					Product - 
					<b>
						{/* No <Link>! this is external reference */}
						<a 
							className="link-hover" 
							rel="noopener nofollow" 
							title={`${props.TechnologyLongName} developer site`} 
							target="_blank" 
							href={props.developer}
						>
							{' ' + _extractWebsiteFromURL(props.developer)}
							&nbsp;&nbsp;&nbsp;&nbsp;
							<span style={{color:'black'}} className="icon text-right fa fa-external_link"/>							
						</a>
					</b>
				</li>
				{
					props.wiki &&
						<li>
							Wikipedia - 
							<b>
								{/* No <Link>! this is external reference */}
								<a
									className="link-hover" 
									rel="noopener nofollow" 
									title={`${props.TechnologyLongName} Wikipedia page`} 
									target="_blank" 
									href={props.wiki}
								>
									{' ' + props.TechnologyShortName}
									&nbsp;&nbsp;&nbsp;&nbsp;
									<span style={{color:'black'}} className="icon text-right fa fa-external_link"/>		
								</a>

							</b>
						</li>
				}
			</ul>
		</>
	)
}

function TechnologySubVersions({props}: {props: TechnologyInfoPageResult}) {
	if (!!props.NoDomainsFound) {
		return null;
	}
	if (!props.TechnologySubVersions || !props.TechnologySubVersions.length) {
		return null
	}

	// DMK: dont know how to create a good mathematic algorithm
    // so just going to hardcode some values
    /*
    let totalCount = 0;

    props.TechnologySubVersions.map((version: TechnologyVersion) => {
		totalCount += version.DomainCount;
    })
		*/    
	let versions: TechnologyVersion[] = [];
//	if (totalCount > 0) 
	{
		props.TechnologySubVersions.map((version) => {
			// DMK: stop hiding versions
//			if ((100 * version.DomainCount / totalCount) > 0.25) 
			{	// 0.25%
				versions.push(version);
			}
		})	
	}

	return (
		<>
		<br/>
		<h4>Major Versions</h4>
		<ul className="list-marked">
			{
				versions.map((technologyVersion, indexKey) => {
					return (
						<li key={indexKey}>
							<Link 
								key={indexKey}
								to = {`/technology/${props.TechnologyURL}/versions`}
								className="link-hover"  
								title={`${props.TechnologyShortName} version ${technologyVersion.Version}`} 
							>
								{technologyVersion.Version + '.xx '}
							</Link> 
							({_printNumber(technologyVersion.SubversionCount)} subversions, {_printNumber(technologyVersion.DomainCount)} domains)
						</li>
					)
				})
			}
		</ul>
		</>
	)
}

function TechnologyRightPanel({props}: {props: TechnologyInfoPageResult}) {
	return (
        <article className="box-bordered">
			<TechnologyRightIcon props={props}/>
			<TechnologyAvailableReports props={props}/>
			<TechnologyAlternativeNames props={props}/>
			<TechnologyInformation props={props}/>
			<TechnologySubVersions props={props}/>
			<CountriesLeadList report={props.TechnologyMonikerToBeRemoved} name={props.TechnologyShortName} reporttype='technology'  geoData={props.NoDomainsFound ? undefined : props.TechnologyGeoData} />
			<TLDLeadList report={props.TechnologyMonikerToBeRemoved} name={props.TechnologyShortName} reporttype='technology' tlds={props.NoDomainsFound ? undefined : props.TLDs}/>
			<TechnologyVersionsLeadList props={props}/>
		</article>
	)							
}

function HasDomainTechnology({props, google}: ComponentPropsWithGoogle) {

	return (
		<>
			<TechnologyBrag props={props}/>

			{/*	<LeadListsTable props={props}/> */ }

			<TechnologyUsageStats props={props}/>
			<TechnologyMarketShare props={props} google={google}/>
			<SecurityStats props={props}/>
			<DistributionByPopularityChart 
				subtitle ={`How the use of ${props.TechnologyShortName} relates to the popularity of websites.`}
				link={props.CanonicalURL} 
				name={props.TechnologyLongName} 
				distribution={props.TechDistributionByPopularity} google={google}/>		
			<TechnologyDevelopments props={props}/>
{/*
			<TechnologyMarketShare props={props} google={google}/>
			<GeoDistribution link={props.CanonicalURL} geoData={props.TechnologyGeoData} google={google}/>

			<TechnologyWebsites props={props}/>

			<TechnologyAlwaysUsedTogether props={props} />
			<TechnologiesOftenUsedTogether props={props} />
			<TechnologyAlternatives props={props}/>
	*/}
		</>
	)
}

function TechnologyBrag({props}: ComponentPropsWithDomains) {
	return (
		<>
			<br/>
			<p>
				We have discovered &nbsp;
				<Link 
					to={props.TechnologyDomains.Button!.Href}
					className="link-hover" 
					title={props.TechnologyDomains.Button!.Title} 
					rel="noopener nofollow" 
				> 
				<b>{_printNumber(props.TechnologyUsage.DomainCount)} live websites</b>
				</Link>	&nbsp;			
				are using {props.TechnologyShortName}.
			</p>
			<p>
				<Link 
					to={`/create-report?technology=${props.TechnologyMonikerToBeRemoved}`}
					className="button button-secondary" 
					title={`Download list of websites using ${props.TechnologyLongName}`} 
					rel="noopener nofollow" 
				>
					Download Lead List
				</Link>
				<br/>
				[<Link 
					to={`/samplereport-salesforce.csv`}
					className="link-hover" target="_blank" 
					title={`Download a sample report`} 
					rel="noopener nofollow" 
				>
					Download a sample report
				</Link>]
			</p>
			<br />
		</>
	)
}

function TechnologyUsageStats({props}: ComponentPropsWithDomains) {

	return (
		<>
			<div className="row row-50">
			<div className="col-12">
				<h3 className="text-left" id="stats">Technology usage statistics				
				<CopyLink link={props.CanonicalURL}  anchor='stats'/> 
				</h3>
				<table className="table-custom table-custom-striped table-custom-primary">
					<tbody>
						<tr>
							<td>Websites using {props.TechnologyShortName}:</td>
							<td>
								<span className="icon text-primary">
									<Link 
										to={`/technology/${props.TechnologyURL}/websites`}
										className="link-hover" 
										title={`List of websites that are using ${props.TechnologyShortName} technology`} 
									>
										<b>{_printNumber(props.TechnologyUsage.DomainCount)}</b> websites
									</Link>
								</span>
							</td>
						</tr>
						{
							props.TechnologyUsage.LastMonthChange &&
								<tr>
									<td>Change from last month:</td>
									<td>
										<span 
											className="icon  mdi mdi-arrow-down" 
											style={{color: 'red'}}
										>
											<b>
												{props.TechnologyUsage.LastMonthChange}%
											</b>
										</span>
									</td>
								</tr>
						}
						{
							props.TechnologyUsage.MostPopularCountry &&
								<tr>
									<td>
										Most popular in:
									</td>
									<td>
										<b>
											<CountryFlagAndName countryCode={props.TechnologyUsage.MostPopularCountry.Country} />
										</b>
									</td>
								</tr>
						}
						<tr>
							<td>
								Market position in {props.CategoryShortName} category:
							</td>
							<td>
								<span className="icon text-primary">
									<b>
									{
											props.IsExclusiveCategory ?
											<>
												#{props.TechnologyMarketShare!.MarketPosition}
											</>:
											<>
												#{props.TechnologyMarketPenetration!.MarketPosition}
											</>
										}
									</b>
								</span>
							</td>
						</tr>
						<tr>
							<td>
								Market {props.IsExclusiveCategory ? 'share' : 'penetration'} in {props.CategoryShortName}:
							</td>
							<td>
								<span className="icon text-primary">
									<b>
										{_printPercent(
											props.IsExclusiveCategory ? 
												props.TechnologyMarketShare!.MarketShare: 
												props.TechnologyMarketPenetration!.MarketPenetration)
										}%
									</b>
								</span>
							</td>
						</tr>
						{
							props.TechnologyUsage.MostPopularVersion &&
							<tr>
								<td>
									Most popular version:
								</td>
								<td>
									<span className="icon text-primary">
										<b>
											<VersionRef 
												technology = {{
													Technology: props.TechnologyShortName,
													TechnologyURL: props.TechnologyURL,
													Version: props.TechnologyUsage.MostPopularVersion													
												}}
											/>
										</b>
									</span>
								</td>
							</tr>
						}
					</tbody>
				</table>
			</div>
		</div>
	</>
	)
}

function TechnologyDevelopments({props}: ComponentPropsWithDomains) {

	if (!props.TechnologyDevelopments) return null;

	return (
		<>
			<div className="row row-50">
				<div className="col-12">
					<h3 className="text-left">Technology adoption</h3>
					Recent changes in {props.TechnologyShortName} usage.			
					<br />
					<table className="table-custom table-custom-striped table-custom-primary">
						<tbody>
							{
								!!props.TechnologyDevelopments.AddedCount &&
								<tr>
									<td>
										Websites that added {props.TechnologyShortName}
									</td>
									<td>
										<span 
											className="icon" 
											style={{color: 'green'}}
										>
											<i className="fa fa-plus" style={{color: 'green'}} title="Technology Added"></i>
											&nbsp;
											<Link 
												to={`/technology/${props.TechnologyURL}/changes`}
												className="link-hover" 
												title={`${props.TechnologyShortName} adoption report`}
											>
												<b>
													{_printNumber(props.TechnologyDevelopments.AddedCount)}
												</b>
											</Link>
										</span>
									</td>
								</tr>
							}
							{
								!!props.TechnologyDevelopments.DroppedCount &&
								<tr>
									<td>
										Websites that dropped {props.TechnologyShortName}
									</td>
									<td>
										<span 
											className="icon" 
											style={{color: 'red'}}
										>		
											<i className="fa fa-minus" style={{color: 'red'}} title="Technology Dropped"></i>									
											&nbsp;
											<Link 
												to={`/technology/${props.TechnologyURL}/changes`}
												className="link-hover" 
												title={`${props.TechnologyShortName} adoption report`}
											>
												<b>
													{_printNumber(props.TechnologyDevelopments.DroppedCount)}
												</b>
											</Link>
										</span>
									</td>
								</tr>
							}
							{
								!!props.TechnologyDevelopments.ChangedVersionCount &&
								<tr>
									<td>Websites that changed version of {props.TechnologyShortName}</td>
									<td>
										<span className="icon text-primary">
											<i className="fa fa-refresh" style={{color: 'blue'}} title="Version Changed"> </i>
											&nbsp;
											<Link 
												to={`/technology/${props.TechnologyURL}/changes`}
												className="link-hover" 
												title={`${props.TechnologyShortName} adoption report`}
											>
												<b>
													{_printNumber(props.TechnologyDevelopments.ChangedVersionCount)}
												</b>
											</Link>
										</span>
									</td>
								</tr>
							}
						</tbody>
					</table>
				</div>
			</div>		
		</>
	)
}

function TechnologyDistribution({props}: ComponentPropsWithDomains) {

	return (
		<>
			<div className="row row-50">
				<div className="col-12">
					<h3 className="text-left" id="popularity">Distribution by websites popularity</h3>
					{props.TechnologyLongName} detection in the top websites by popularity
					<table className="table-custom table-custom-striped table-custom-primary">
						<tbody>
							<tr>
								<td>Top 10k sites</td>
								<td><span className="icon text-primary">{_printNumber(props.TechDistributionByPopularity.Top10KUsage)} websites</span></td>
							</tr>
							<tr>
								<td>Top 100k sites</td>
								<td><span className="icon text-primary">{_printNumber(props.TechDistributionByPopularity.Top100KUsage)} websites</span></td>
							</tr>
							<tr>
								<td>Top 1m sites</td>
								<td><span className="icon text-primary">{_printNumber(props.TechDistributionByPopularity.Top1MUsage)} websites</span></td>
							</tr>
							<tr>
								<td>Top 10m sites</td>
								<td><span className="icon text-primary">{_printNumber(props.TechDistributionByPopularity.Top10MUsage)} websites</span></td>
							</tr>
						</tbody>
					</table>
				</div>
			</div>		
		</>		
	)
}

function TechnologyWebsites({props}: ComponentPropsWithDomains) {

	return (
		<div className="row row-50">
			<div className="col-md-12 col-lg-12 col-xl-12">
				<DomainTable domainList={props.TechnologyDomains} link={props.CanonicalURL}/>
			</div>
		</div>	
	)
}

function TechnologyAlwaysUsedTogether({props}: ComponentPropsWithDomains) {

	if (!props.TechnologiesAlwaysUsedTogether) return null; 

	return (
		<>
			<div className="row row-50">
				<div className="col-12">
					<h3 className="text-left">Always Used together</h3>
					<br />
					<table className="table-custom table-custom-striped table-custom-primary">
						<tbody>
							{
								props.TechnologiesAlwaysUsedTogether.map((technologyUsedTogether, indexKey) => {
									return (
									<tr key={indexKey}>
										<td>
											<Link 
												to={`/technology/${technologyUsedTogether.TechnologyURL}`}
												className="link-hover" 
												title={`${technologyUsedTogether.TechnologyShortName} market share and usage statistic`}  
											>
												<div>
													<TechnologySmallIcon 
														technologyIcon={technologyUsedTogether.TechnologyIcon} 
														technology={technologyUsedTogether.TechnologyShortName}
													/>
													<b>
														{' '}{technologyUsedTogether.TechnologyShortName}:
													</b> 
														{' '}Total{' '}
													<b>
														{_printNumber(technologyUsedTogether.TechDomains)}
													</b> 
													{' '}domains found using {technologyUsedTogether.TechnologyShortName}
												</div>
												<span 
													className={`icon text-primary mdi ${technologyUsedTogether.CategoryIcon}`}>
													{'    '}
												</span>
												<span>{' '}Category - 
													<b>
														{' '}{technologyUsedTogether.CategoryShortName}
													</b>
													. {_printNumber(technologyUsedTogether.CategoyDomains)} domains found using {technologyUsedTogether.CategoryShortName}
												</span>
											</Link>
										</td>
									</tr>
									)
								})
							}
						</tbody>
					</table>
				</div>
			</div>       
			<br/>	
		</>
	)
}

// function TechnologiesOftenUsedTogether({props}: ComponentPropsWithDomains) {

// 	if (!props.TechnologiesOftenUsedTogether || props.TechnologiesOftenUsedTogether.length === 0) {
// 		return null;
// 	}

// 	return (<>
		
// 		<div className="row row-50">
// 			<div className="col-12">
// 				<h3 className="text-left">{props.TechnologyLongName} often used together with</h3>
// 				<br/>
// 				<table className="table-custom table-custom-striped table-custom-primary">
// 					<tbody>
// 						{
// 							props.TechnologiesOftenUsedTogether.map((technology) => {

// 								return (
// 									<tr>
// 										<td>
// 											<div>
// 												<Link 
// 													to={`/technology/${technology.TechnologyURL}`}
// 													className="link-hover" 
// 													title={`${technology.TechnologyShortName} market share and usage statistic`} 
// 												>
// 													<TechnologySmallIcon 
// 														technologyIcon={technology.TechnologyIcon} 
// 														technology={technology.TechnologyShortName}
// 													/>													
// 													<b>{technology.TechnologyShortName}</b>
// 												</Link>
// 											</div>
// 											<span>
// 												Category -
// 												<span className={`icon text-primary mdi ${props.CategoryIcon}`}></span>
// 												<b>{props.CategoryShortName}</b>.
// 											</span>                                
// 											<div>
// 												<Link 
// 													to={`/technology/${technology.TechnologyURL}`}
// 													className="link-hover" 
// 													title={`${technology.TechnologyShortName} market share and usage statistic`} 
// 												>
// 													{technology.TechnologyShortName}
// 												</Link>
// 												is used together with <b>{props.TechnologyShortName}</b> on {_printNumber(technology.UsedTogetherCount)} domains. Total {_printNumber(technology.TechDomains)} domains found using {technology.TechnologyShortName}
// 											</div>
// 										</td>
// 									</tr>								
// 								)
// 							})
// 						}    
// 					</tbody>
// 				</table>
// 			</div>
// 		</div>       
// 		<br/>

// 		</>)
// }

function TechnologyAlternatives({props}: ComponentPropsWithDomains) {

	if (!props.TechnologyAlternatives) return null;

	return (<>

		<div className="row row-50">
			<div className="col-12">
				<h3 className="text-left" id="altern">Alternative Technologies
				<CopyLink link={props.CanonicalURL}  anchor='altern'/> 
				</h3>
				<br/>
				<table className="table-custom table-custom-striped table-custom-primary">
					<tbody>
						{
							props.TechnologyAlternatives.map((alternative, indexKey) => {
								return (
								<tr key={indexKey}>
									<td key={indexKey}>
										<span key={indexKey}>
											<div key={indexKey}>
												<Link 	
													key={indexKey}
													to={`/technology/${alternative.TechnologyURL}`}
													className="link-hover" 
													title={`${alternative.TechnologyShortName} market share and usage statistic`} 
												>
													<TechnologySmallIcon 
														technologyIcon={alternative.TechnologyIcon} 
														technology={alternative.TechnologyShortName}
													/>													
													{' '}<b>{alternative.TechnologyShortName}:</b>
												</Link>
												{' '}Total {_printNumber(alternative.TechDomains)} domains found using {alternative.TechnologyShortName}
											</div>
										</span>
									</td>
								</tr>								)
							})  
						}  
					</tbody>
				</table>
			</div>
		</div>       
	</>)
}

function TechnologyVersionsLeadList({props}: {props: TechnologyInfoPageResult}) {

	if (props.NoDomainsFound) {
		return null;
	}
	if (!props.TechnologyVersions || !props.TechnologyVersions.length) {
		return null
	}
	
	const versions: TechnologyVersion[] = [];
	let i: number = 0;
	let length: number = (props.TechnologyVersions.length < 10) ? props.TechnologyVersions.length : 10;
	for (; i < length; i++) {
		let technologyVersion = props.TechnologyVersions[i];

		const versionToAdd: TechnologyVersion = {
			Version: technologyVersion.Version, 
			DomainCount: technologyVersion.DomainCount
		}
		versions.push(versionToAdd);
	}
	// {`Create report with websites using ${props.technologyLongName} version ${version.Version}`}	

	return (
		<>
			<br/>
			<h4>{props.TechnologyLongName} Versions</h4>
			sorted by popularity
			<table width="100%">
				<tbody>
				{
					versions.map((version, indexKey) => {
						return (
							<tr  key={indexKey}>
								<td width="40%">{version.Version}</td>
								<td width="60%">	
									<Link 
										className="link-hover word-break" 
										title={`Download Lead List`}									
										to = {`/create-report?technology=${props.TechnologyMonikerToBeRemoved}&version=${version.Version}`}
									>
										<b>{_printNumber(version.DomainCount)}</b> websites
									</Link>
								</td>
						
							</tr>
							)
						}
					)
				}
				</tbody>
			</table>
			<br/>
			See all&nbsp;
			<Link 
				to = {`/technology/${props.TechnologyURL}/versions`}
				className="link-hover"  
				title={`${props.TechnologyShortName} versions list`} 
			><b>{props.TechnologyLongName} versions</b> ...
			</Link> 
		</>
	)	


}

function LeadListsTable({props}: ComponentProps) {

	if (!props) {
		return null
	}
	let name = props.TechnologyLongName
//	<div className="row row-50">
//	<div className="col-12">
//	</div>
//	</div>

	return (
		<>	
			<br/>
			<hr></hr>		
			<br/>
			<h4>Lead Lists ideas</h4>
			Download targeted list of live websites based on changes in usage of {name} technology 
			{props.IsExclusiveCategory ? ` or its competitors from ${props.CategoryShortName}` : ''}

			<table className="table-custom table-custom-striped table-custom-primary">
				<tbody>
					<LeadList props={props} title={`Websites that started using ${name} in the last 3 month`} count={`2,462`}/>
					<LeadList props={props} title={`Websites that stopped using ${name} in the last 3 month`} count={`1,345`}/>
					<LeadList props={props} title={`Websites that do not use ${props.CategoryShortName} technologies but used it in the past.`} count={`462`}/>
					<LeadList props={props} title={`Websites that started using competing ${props.CategoryShortName} technology 9-12 month ago`} count={`562`}/>
					<LeadList props={props} title={`Websites that started using ${name} 9-12 month ago`} count={`354`}/>
					<LeadList props={props} title={`Websites that switched to ${name} from competing ${props.CategoryShortName} technology`} count={`7,462`}/>
					<LeadList props={props} title={`Websites that switched from ${name} to competing ${props.CategoryShortName} technology `} count={`22,462`}/>
				</tbody>
			</table>

		</>
	)
}

function LeadList({props, title, count}: LeadListProps) {

	if (!title || !count) {
		return null
	}
	return (
		<>	
			<tr>
				<td>{title}&nbsp;</td>
				<td align="right">
					<Link 
						className="link-hover word-break" 
						title={`Download Lead List`}									
						to = {`/create-report?technology=${props.TechnologyMonikerToBeRemoved}`}
					><b>{count}</b> websites 
					</Link>						
				</td>
			</tr>
		</>
	)
}

function compareCVEbyCount(a: CVEData | null, b: CVEData | null): number {
	// workaround null element
    if (a == null || b == null) {
		return 0;
        //return a < b;
	}
    if (a.DomainCount > b.DomainCount)
        return -1;
    if (a.DomainCount < b.DomainCount)
        return 1;

    return 0;
}
function SecurityStats({props}: ComponentPropsWithDomains) {

	const cves: CVEData[] | undefined = props.CVEs
	const link: string = props.CanonicalURL!;
	if (cves == undefined) return null;

	let l = cves.sort((a, b) => compareCVEbyCount(a, b));
	const partialList: boolean = l.length > 10;
	if (partialList) {
		l = l.slice(0,10);
	}

	return (
		<>

	<div className="row row-50">
		<div className="col-12">   
		<h3 id="vulnerabilites">Security vulnerabilites
		<CopyLink link={link}  anchor='vulnerabilites'/> 
		</h3>    
		{
			!!partialList && 
			<>Top 10 most common security vulnerabilities associated with {props.TechnologyShortName} </>
		}
		{
			!partialList &&
			<>Known security issues associated with {props.TechnologyShortName}</>
		}
		<br/>    
		<div className="table-outer table-rounded table-responsive-lg">
			<table className="table-custom table-custom-striped table-custom-primary  ">
					<thead>
					<tr>
							<th style={{width: '10%'}}>Published</th>
							<th style={{width: '25%'}}>CVE</th> 
							<th style={{width: '57%'}}>Title</th> 
							<th style={{width: '8%'}}>Domains</th>
					</tr>
					</thead>
							<tbody>
									{
									l.map((cve, indexKey) => {
											return (
													<CVERow cve={cve} key={indexKey}/>
											)
									})
							}	
					</tbody>
			</table>
		</div>		
		</div>		
	</div>		
		</>
	)
}

function CVERow({cve, requireAuthentication}: CVERowProps) {
	// icon, tech, version, category, date, count
	return (
		<>
			<tr>         
				<td>
					{_printShortDate(cve.DatePublished)}
				</td>     
				<td>
					{cve.CVEId}
				</td>    
			<td>	       
				{/* inconcistency in CVEId naming -with or without CVE prefix* */}              
					<Link 
							to={`/cve/${cve.CVEId}`} 
							className="link-hover" 
					>
						<CveTitle cve={cve} />
					</Link> 							
			</td>
			<td>
					{_printNumber(cve.DomainCount)}
			</td>
			</tr>		
		</>
	)
}
	


function TechnologyBottomPanel({props, google}: {props: TechnologyInfoPageResult, google: wts_approved_any}) {
	if (props.NoDomainsFound) return (<></>);
	/*	
	let faqs = [];

	if (props.faqs) {
		let name = (props.technologyLongName != undefined) ? props.technologyLongName : props.technologyShortName;
		let alternatives = '';
		let alternativeshtml = '';
		let cves = '';
		let cveshtml = '';
		let cvecount = 0;
		let safeVersion = '';
	
		if (props.CVEs != undefined) {
			props.CVEs.map((cve, indexKey) => {
				cvecount++;
				if (indexKey <= 3) {
					if (cves != '') { 
						cveshtml += (indexKey == 3) ? ', and ' : ', ';
						cves += (indexKey == 3) ? ', and ' : ', ';
					}
					cveshtml += `<a class="link-hover" href="/cve/${cve.CVEId}">${cve.CVEId}</a>`;
					cves += `${cve.CVEId}`;
				}		
			})
		}
		if (cvecount && props.TechnologySubVersions)
		{
			props.TechnologySubVersions.map((version, indexKey) => {
				// TEMP - this is wrong way to detect safe version
		//		safeVersion = version.Version;
			})
		}
	
		if (props.TechnologyAlternatives) {
			props.TechnologyAlternatives.map((alternative, indexKey) => {
				if (indexKey <= 3) {
					if (alternatives != '') { 
						alternatives += (indexKey == 3) ? ', and ' : ', ';
						alternativeshtml += (indexKey == 3) ? ', and ' : ', ';
					}
					alternativeshtml += `<a class="link-hover" href="/technology/${alternative.TechnologyURL}">${alternative.TechnologyShortName}</a>`;
					alternatives += `${alternative.TechnologyShortName}`;
				}
			})
		}		
		let categories = '';
		let categorieshtml = '';
		let category = '';
		let categoryhtml = '';
		let catCount = 0;
		if (props.categories) {
			props.categories.map((cat, indexKey) => {
				if (categories != '') { 
					categories += (indexKey + 1 == props.categories.length) ? ', and ' : ', ';
					categorieshtml += (indexKey + 1 == props.categories.length) ? ', and ' : ', ';
				}
				categorieshtml += `<a class="link-hover" href="/technology-type/${cat.URL}">${cat.CategoryShortName}</a>`;
				categories += `${cat.CategoryShortName}`;
				if (indexKey === 0) {
					category = categories;
					categoryhtml = categorieshtml;
				}
				catCount++;
			})
		}			
		let counthtml = `<a class="link-hover" href="/technology/${props.TechnologyURL}/websites"><b>${_printNumber(props.TechnologyUsage.DomainCount)}</b></a>`;
		let count = `${_printNumber(props.TechnologyUsage.DomainCount)}`;
		props.faqs.forEach(f => {
			if (f.question.indexOf('market share') != -1 && !props.IsExclusiveCategory) {
				// skip market share question for none-market share tech
				// (market penetration ?)
				return;
			}
			if (f.answer.indexOf('%cves%') != -1) {
				if (cvecount <= 1) {
					// skip security question for products without CVEs
					return;
				}
				if ((f.answer.indexOf('%version%') != -1) != (safeVersion.length)) {
					// version question should match existence of version
					return;
				}
			}
			if (f.answer.indexOf('%cve%') != -1) {
				if (cvecount != 1) {
					// skip security question for products without CVEs
					return;
				}
				if ((f.answer.indexOf('%version%') != -1) != (safeVersion.length)) {
					// version question should match existence of version
					return;
				}
			}	

			// --	
			f.question = f.question.replaceAll('%tech%',name);
			f.answer = f.answer.replaceAll('%tech%',name);
			f.answer = f.answer.replaceAll('%version%',safeVersion);
			if (props.TechnologyMarketShare != undefined) {
				f.answer = f.answer.replaceAll('%share%',`${_printPercent(props.TechnologyMarketShare.MarketShare)}%`);
			}
			// --
			f.questionhtml = f.question;		
			f.answerhtml = f.answer;	

			// --
			if (catCount == 1) {
				// skip multipel categories question for products with single category
				if (f.answer.indexOf('%categories%') != -1) return;
			}
			if (catCount >= 2) {
				// skip multipel categories question for products with single category
				if (f.answer.indexOf('%category%') != -1) return;
			}
			f.answer = f.answer.replaceAll('%categories%', categories);
			f.answer = f.answer.replaceAll('%domains%',count);
			f.answer = f.answer.replaceAll('%category%',category);
			f.answer = f.answer.replaceAll('%alternatives%', alternatives);
			if (cves != '') {
				f.answer = f.answer.replaceAll('%cve%',cves);
				f.answer = f.answer.replaceAll('%cves%',cves);
			}		
			// --	
			f.answerhtml = f.answerhtml.replaceAll('%categories%', categorieshtml);
			f.answerhtml = f.answerhtml.replaceAll('%domains%',counthtml);
			f.answerhtml = f.answerhtml.replaceAll('%category%',categoryhtml);
			f.answerhtml = f.answerhtml.replaceAll('%alternatives%', alternativeshtml);
			if (cveshtml != '') {
				f.answerhtml = f.answerhtml.replaceAll('%cve%',cveshtml);
				f.answerhtml = f.answerhtml.replaceAll('%cves%',cveshtml);
			}
			faqs.push(f);
		})		
	}
	*/

	return (
		<>
			<GeoDistribution link={props.CanonicalURL} geoData={props.TechnologyGeoData} google={google}/>
			<TechnologyWebsites props={props}/>

			<TechnologyAlwaysUsedTogether props={props} />
			{/*<TechnologiesOftenUsedTogether props={props} />*/}
			<TechnologyAlternatives props={props}/>
			<FAQ faqs={props.faqs} expanded='1'/>
		</>
	)
}

